<script setup>
  import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
  import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { STATUS_CLASSES, useNavigationSearch } from "@utils/composables/useNavigationSearch.js";

  const { open, query, pageQuery, results, loading, expandedSurvey, selectedPageIndex, filteredPages, handleSelect, handleSelectPage, handleOpenChange } =
    useNavigationSearch();
</script>

<template>
  <Dialog
    :open="open"
    @update:open="handleOpenChange">
    <DialogContent
      class="w-[40vw] overflow-hidden p-0"
      :show-close-button="false">
      <DialogHeader class="sr-only">
        <DialogTitle>Search a survey</DialogTitle>
        <DialogDescription>Navigate to a survey by typing its name</DialogDescription>
      </DialogHeader>
      <Command>
        <CommandInput
          :placeholder="expandedSurvey ? 'Filter pages...' : 'Search a survey...'"
          @update:model-value="(val) => (expandedSurvey ? (pageQuery = val) : (query = val))" />

        <!-- Page selection mode (TAB on a survey item) -->
        <div v-if="expandedSurvey">
          <div class="border-b px-3 py-2 text-xs text-muted-foreground">
            <span class="font-medium text-foreground">{{ expandedSurvey.name }}</span>
            <span class="ml-2">— choose a page</span>
          </div>
          <EmptyState
            v-if="filteredPages.length === 0"
            size="sm"
            title="No pages found" />
          <div
            v-else
            class="p-1">
            <button
              v-for="(page, i) in filteredPages"
              :key="page.label"
              class="flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
              :class="{ 'bg-accent text-accent-foreground': selectedPageIndex === i }"
              @click="handleSelectPage(page)">
              <component
                :is="page.icon"
                class="size-4 shrink-0" />
              {{ page.label }}
              <span
                v-if="page.badge"
                class="ml-1 rounded px-1 py-0.5 text-xs font-medium bg-status-info-soft text-status-info-strong">
                {{ page.badge }}
              </span>
            </button>
          </div>
          <div class="border-t px-3 py-2 text-xs text-muted-foreground">↑↓ navigate · ↵ open · ⇥/⎋ back</div>
        </div>

        <!-- Normal survey list -->
        <template v-else>
          <EmptyState
            v-if="query.length < 3"
            class="py-6"
            title="Type at least 3 characters to search" />
          <EmptyState
            v-else-if="loading && results.length === 0"
            class="py-6"
            title="Searching..." />
          <EmptyState
            v-else-if="results.length === 0"
            class="py-6"
            title="No survey found" />
          <CommandList v-else>
            <CommandGroup>
              <CommandItem
                v-for="survey in results"
                :key="survey.token"
                :value="survey.token"
                :data-token="survey.token"
                @select="() => handleSelect(survey)">
                <span class="flex-1 truncate">{{ survey.name }}</span>
                <span class="ml-2 shrink-0 rounded px-1.5 py-0.5 text-xs text-muted-foreground">⇥</span>
                <span
                  class="ml-1 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="STATUS_CLASSES[survey.status] ?? 'bg-status-success-soft text-status-success-strong'">
                  {{ survey.status }}
                </span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </template>
      </Command>
    </DialogContent>
  </Dialog>
</template>
